import { useEffect, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ticketKeys } from '@/api/hooks/useTickets';
import { dashboardKeys } from '@/api/hooks/useDashboard';
import { PICKUP_QUEUE_KEY } from '@/api/hooks/usePackagePickup';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import {
  signalRService,
  type TicketNotification,
  type StatusChangeNotification,
  type DashboardUpdateNotification,
  type UserNotification,
  type LowStockNotification,
} from '@/lib/signalr';

/**
 * Reactive connection state, kept in sync via signalRService's connection-change event so it
 * stays correct across reconnects/drops too (a plain isConnected() snapshot only ever reflected
 * the state at the instant it was read).
 */
function useSignalRConnected(): boolean {
  const [connected, setConnected] = useState(() => signalRService.isConnected());

  useEffect(() => {
    return signalRService.onConnectionChange(setConnected);
  }, []);

  return connected;
}

/**
 * Hook for managing SignalR connection lifecycle
 */
export function useSignalRConnection() {
  const { token, isAuthenticated } = useAuthStore();
  const isConnected = useSignalRConnected();
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      signalRService.stop();
      return;
    }

    const connect = async () => {
      try {
        await signalRService.start(token);
        setError(null);
      } catch (err) {
        setError(err as Error);
      }
    };

    connect();

    return () => {
      signalRService.stop();
    };
  }, [isAuthenticated, token]);

  return { isConnected, error };
}

/**
 * Hook for subscribing to ticket updates
 */
export function useTicketUpdates(ticketId: string | undefined) {
  const [lastUpdate, setLastUpdate] = useState<TicketNotification | null>(null);
  const [statusChange, setStatusChange] = useState<StatusChangeNotification | null>(null);
  const isConnected = useSignalRConnected();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!ticketId) return;

    // Reporting the event in local state is not enough on its own: the page renders from the
    // TanStack Query cache, which has no idea anything changed and would keep serving the stale
    // ticket until something refetched it. Invalidating is what actually makes the screen update -
    // without it a change made elsewhere (the customer approving from their tracking link, another
    // staff member on another machine) stays invisible until a manual refresh.
    const refreshTicketData = () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
      // Lists and the queue's status badges show this ticket too, so they're stale now as well.
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
    };

    // Both sides carry the same GUID, but one comes from the URL and the other from the server's
    // Guid.ToString() - compare case-insensitively so a casing difference can never make this
    // silently stop working (the failure mode would be "live updates just don't happen", with
    // nothing logged anywhere).
    const isThisTicket = (incomingId: string) =>
      incomingId?.toLowerCase() === ticketId.toLowerCase();

    // Registering event listeners is safe even before the handshake finishes - the service
    // stores them and attaches (or re-attaches) them once actually connected.
    const unsubUpdate = signalRService.on<TicketNotification>('TicketUpdated', (data) => {
      if (isThisTicket(data.ticketId)) {
        setLastUpdate(data);
        refreshTicketData();
      }
    });

    const unsubStatus = signalRService.on<StatusChangeNotification>('TicketStatusChanged', (data) => {
      if (isThisTicket(data.ticketId)) {
        setStatusChange(data);
        refreshTicketData();
      }
    });

    // A push is only heard while connected. Anything that happened during a drop - a colleague's
    // edit, a customer approving from the tracking link - never arrives as an event once the
    // connection comes back; the client only reacts to what's pushed *after* it reconnects, not to
    // what it missed while it was gone. Treating every reconnect as "assume something changed" and
    // refetching closes that gap without the user having to notice a page went stale and reload it
    // themselves - which is the whole point of a live view on a closed staff system.
    const unsubConnectionChange = signalRService.onConnectionChange((connected) => {
      if (connected) refreshTicketData();
    });

    // The server-side group subscription is a real hub invocation and needs an actual connection.
    if (isConnected) {
      signalRService.subscribeToTicket(ticketId);
    }

    return () => {
      if (isConnected) {
        signalRService.unsubscribeFromTicket(ticketId);
      }
      unsubUpdate();
      unsubStatus();
      unsubConnectionChange();
    };
  }, [ticketId, isConnected, queryClient]);

  return { lastUpdate, statusChange };
}

/**
 * Hook for subscribing to dashboard updates
 */
export function useDashboardUpdates() {
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<DashboardUpdateNotification | null>(null);
  const isConnected = useSignalRConnected();
  const queryClient = useQueryClient();

  useEffect(() => {
    // The dashboard also polls every 5 minutes (useDashboard.ts), so this is about latency rather
    // than correctness: a ticket taken in now shows up immediately instead of up to five minutes
    // later. Invalidating the whole `dashboard` key covers all four of its queries.
    const refreshDashboard = () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    };

    const unsubRefresh = signalRService.on<{ Type: string }>('DashboardRefresh', () => {
      setShouldRefresh(true);
      refreshDashboard();
    });

    const unsubUpdate = signalRService.on<DashboardUpdateNotification>('DashboardUpdate', (data) => {
      setLastUpdate(data);
      setShouldRefresh(true);
      refreshDashboard();
    });

    // See the matching comment in useTicketUpdates: a reconnect only resumes hearing *future*
    // pushes, so anything that happened during the drop needs an explicit catch-up refetch here too.
    const unsubConnectionChange = signalRService.onConnectionChange((connected) => {
      if (connected) refreshDashboard();
    });

    if (isConnected) {
      signalRService.subscribeToDashboard();
    }

    return () => {
      if (isConnected) {
        signalRService.unsubscribeFromDashboard();
      }
      unsubRefresh();
      unsubUpdate();
      unsubConnectionChange();
    };
  }, [isConnected, queryClient]);

  const clearRefresh = useCallback(() => {
    setShouldRefresh(false);
  }, []);

  return { shouldRefresh, lastUpdate, clearRefresh };
}

/**
 * Hook for listening to all ticket events (for list pages)
 */
export function useTicketListUpdates() {
  const [newTicket, setNewTicket] = useState<TicketNotification | null>(null);
  const [updatedTicket, setUpdatedTicket] = useState<TicketNotification | null>(null);
  const [statusChange, setStatusChange] = useState<StatusChangeNotification | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    // Reporting the event in state isn't enough on its own - the pages render from the TanStack
    // Query cache, which has no idea anything changed. Invalidating is what actually redraws the
    // list and the queue when a colleague takes in a device or moves a ticket along.
    const refreshTicketLists = () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ticketKeys.statusCounts() });
    };

    const unsubCreated = signalRService.on<TicketNotification>('TicketCreated', (data) => {
      setNewTicket(data);
      refreshTicketLists();
    });

    const unsubUpdated = signalRService.on<TicketNotification>('TicketUpdated', (data) => {
      setUpdatedTicket(data);
      refreshTicketLists();
    });

    const unsubStatus = signalRService.on<StatusChangeNotification>('TicketStatusChanged', (data) => {
      setStatusChange(data);
      refreshTicketLists();
    });

    // See the matching comment in useTicketUpdates: a reconnect only resumes hearing *future*
    // pushes, so a ticket created or moved while this tab was disconnected - the exact case that
    // motivated this fix - needs an explicit catch-up refetch here too.
    const unsubConnectionChange = signalRService.onConnectionChange((connected) => {
      if (connected) refreshTicketLists();
    });

    return () => {
      unsubCreated();
      unsubUpdated();
      unsubStatus();
      unsubConnectionChange();
    };
  }, [queryClient]);

  const clearNewTicket = useCallback(() => setNewTicket(null), []);
  const clearUpdatedTicket = useCallback(() => setUpdatedTicket(null), []);
  const clearStatusChange = useCallback(() => setStatusChange(null), []);

  return {
    newTicket,
    updatedTicket,
    statusChange,
    clearNewTicket,
    clearUpdatedTicket,
    clearStatusChange,
  };
}

/**
 * Hook for the staff package-pickup queue page - the backend sends one coarse "PickupQueueUpdated"
 * signal on any create/status change (mirrors useTicketListUpdates' refetch-on-event approach)
 * rather than a per-field payload, so this just needs to invalidate and let useQuery refetch.
 */
export function usePickupQueueUpdates() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const refreshQueue = () => {
      queryClient.invalidateQueries({ queryKey: [PICKUP_QUEUE_KEY] });
    };

    const unsub = signalRService.on('PickupQueueUpdated', refreshQueue);

    // See the matching comment in useTicketListUpdates: a reconnect only resumes hearing *future*
    // pushes, so a request created or moved while disconnected needs an explicit catch-up refetch.
    const unsubConnectionChange = signalRService.onConnectionChange((connected) => {
      if (connected) refreshQueue();
    });

    return () => {
      unsub();
      unsubConnectionChange();
    };
  }, [queryClient]);
}

/**
 * Hook for personal user notifications
 */
export function useUserNotifications() {
  const addNotification = useNotificationStore((state) => state.addNotification);

  useEffect(() => {
    const unsubUser = signalRService.on<UserNotification>('UserNotification', (data) => {
      addNotification({
        title: data.title,
        message: data.message,
        type: data.type,
        actionUrl: data.actionUrl,
      });
    });

    const unsubAssigned = signalRService.on<TicketNotification>('TicketAssignedToYou', (data) => {
      addNotification({
        title: 'קריאה חדשה הוקצתה לך',
        message: `קריאה #${data.ticketNumber} - ${data.customerName} (${data.deviceName})`,
        type: 'info',
        actionUrl: `/tickets/${data.ticketId}`,
      });
    });

    return () => {
      unsubUser();
      unsubAssigned();
    };
  }, [addNotification]);
}

/**
 * Hook for low stock alerts
 */
export function useLowStockAlerts() {
  const [alerts, setAlerts] = useState<LowStockNotification[]>([]);
  const addNotification = useNotificationStore((state) => state.addNotification);

  useEffect(() => {
    const unsub = signalRService.on<LowStockNotification>('LowStockAlert', (data) => {
      setAlerts((prev) => [...prev, data]);
      addNotification({
        title: 'התראת מלאי נמוך',
        message: `${data.partName} (${data.sku}) - נותרו ${data.currentStock} יחידות`,
        type: 'warning',
        actionUrl: `/inventory?sku=${data.sku}`,
        persistent: true,
      });
    });

    return () => {
      unsub();
    };
  }, [addNotification]);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  return { alerts, clearAlerts };
}
